/**
 * MagicBI Design System - Theme Utilities
 * 
 * Utility functions for theme management, CSS custom properties,
 * and theme validation.
 */

import type { Theme, CSSCustomProperties, ThemeValidation } from '../types/theme';

// Convert theme to CSS custom properties
export const themeToCSSCustomProperties = (theme: Theme): Partial<CSSCustomProperties> => {
  return {
    // Colors - Primary
    '--color-primary-50': theme.colors.primary[50],
    '--color-primary-100': theme.colors.primary[100],
    '--color-primary-200': theme.colors.primary[200],
    '--color-primary-300': theme.colors.primary[300],
    '--color-primary-400': theme.colors.primary[400],
    '--color-primary-500': theme.colors.primary[500],
    '--color-primary-600': theme.colors.primary[600],
    '--color-primary-700': theme.colors.primary[700],
    '--color-primary-800': theme.colors.primary[800],
    '--color-primary-900': theme.colors.primary[900],

    // Colors - Grey
    '--color-grey-50': theme.colors.grey[50],
    '--color-grey-100': theme.colors.grey[100],
    '--color-grey-200': theme.colors.grey[200],
    '--color-grey-300': theme.colors.grey[300],
    '--color-grey-400': theme.colors.grey[400],
    '--color-grey-500': theme.colors.grey[500],
    '--color-grey-600': theme.colors.grey[600],
    '--color-grey-700': theme.colors.grey[700],
    '--color-grey-800': theme.colors.grey[800],
    '--color-grey-900': theme.colors.grey[900],

    // Colors - Background
    '--color-background-primary': theme.colors.background.primary,
    '--color-background-secondary': theme.colors.background.secondary,
    '--color-background-tertiary': theme.colors.background.tertiary,
    '--color-background-inverse': theme.colors.background.inverse,

    // Colors - Surface
    '--color-surface-primary': theme.colors.surface.primary,
    '--color-surface-secondary': theme.colors.surface.secondary,
    '--color-surface-tertiary': theme.colors.surface.tertiary,
    '--color-surface-raised': theme.colors.surface.raised,
    '--color-surface-overlay': theme.colors.surface.overlay,

    // Colors - Border
    '--color-border-subtle': theme.colors.border.subtle,
    '--color-border-default': theme.colors.border.default,
    '--color-border-strong': theme.colors.border.strong,
    '--color-border-inverse': theme.colors.border.inverse,

    // Colors - Text
    '--color-text-primary': theme.colors.text.primary,
    '--color-text-secondary': theme.colors.text.secondary,
    '--color-text-tertiary': theme.colors.text.tertiary,
    '--color-text-disabled': theme.colors.text.disabled,
    '--color-text-inverse': theme.colors.text.inverse,
    '--color-text-link': theme.colors.text.link,
    '--color-text-link-hover': theme.colors.text.linkHover,

    // Colors - Interactive
    '--color-interactive-primary': theme.colors.interactive.primary,
    '--color-interactive-primary-hover': theme.colors.interactive.primaryHover,
    '--color-interactive-primary-active': theme.colors.interactive.primaryActive,
    '--color-interactive-primary-disabled': theme.colors.interactive.primaryDisabled,

    '--color-interactive-secondary': theme.colors.interactive.secondary,
    '--color-interactive-secondary-hover': theme.colors.interactive.secondaryHover,
    '--color-interactive-secondary-active': theme.colors.interactive.secondaryActive,

    '--color-interactive-tertiary': theme.colors.interactive.tertiary,
    '--color-interactive-tertiary-hover': theme.colors.interactive.tertiaryHover,
    '--color-interactive-tertiary-active': theme.colors.interactive.tertiaryActive,

    // Colors - Status
    '--color-success-50': theme.colors.success[50],
    '--color-success-500': theme.colors.success[500],
    '--color-success-600': theme.colors.success[600],
    '--color-success-text': theme.colors.success.text,

    '--color-warning-50': theme.colors.warning[50],
    '--color-warning-500': theme.colors.warning[500],
    '--color-warning-600': theme.colors.warning[600],
    '--color-warning-text': theme.colors.warning.text,

    '--color-error-50': theme.colors.error[50],
    '--color-error-500': theme.colors.error[500],
    '--color-error-600': theme.colors.error[600],
    '--color-error-text': theme.colors.error.text,

    '--color-info-50': theme.colors.info[50],
    '--color-info-500': theme.colors.info[500],
    '--color-info-600': theme.colors.info[600],
    '--color-info-text': theme.colors.info.text,

    // Spacing
    '--spacing-xs': theme.spacing.semantic.inset.xs,
    '--spacing-sm': theme.spacing.semantic.inset.sm,
    '--spacing-md': theme.spacing.semantic.inset.md,
    '--spacing-lg': theme.spacing.semantic.inset.lg,
    '--spacing-xl': theme.spacing.semantic.inset.xl,
    '--spacing-2xl': theme.spacing.semantic.inset['2xl'],
    '--spacing-3xl': theme.spacing.semantic.inset['3xl'],

    // Typography
    '--font-family-sans': theme.typography.body.medium.fontFamily,
    '--font-family-mono': theme.typography.code.medium.fontFamily,

    // Shadows
    '--shadow-xs': theme.shadows.elevation[1],
    '--shadow-sm': theme.shadows.elevation[2],
    '--shadow-md': theme.shadows.elevation[3],
    '--shadow-lg': theme.shadows.elevation[4],
    '--shadow-xl': theme.shadows.elevation[5],
    '--shadow-2xl': theme.shadows.elevation[6],

    '--shadow-focus': theme.shadows.interactive.focus,

    // Border radius
    '--border-radius-none': theme.borders.radius.none,
    '--border-radius-xs': theme.borders.radius.xs,
    '--border-radius-sm': theme.borders.radius.sm,
    '--border-radius-md': theme.borders.radius.md,
    '--border-radius-lg': theme.borders.radius.lg,
    '--border-radius-xl': theme.borders.radius.xl,
    '--border-radius-2xl': theme.borders.radius['2xl'],
    '--border-radius-3xl': theme.borders.radius['3xl'],
    '--border-radius-full': theme.borders.radius.full,

    // Transitions
    '--transition-fast': `all ${theme.transitions.durations.fast} ${theme.transitions.easing.smooth}`,
    '--transition-normal': `all ${theme.transitions.durations.normal} ${theme.transitions.easing.smooth}`,
    '--transition-slow': `all ${theme.transitions.durations.slow} ${theme.transitions.easing.smooth}`,

    // Z-index
    '--z-index-dropdown': theme.zIndex.dropdown.toString(),
    '--z-index-sticky': theme.zIndex.sticky.toString(),
    '--z-index-banner': theme.zIndex.banner.toString(),
    '--z-index-overlay': theme.zIndex.overlay.toString(),
    '--z-index-modal': theme.zIndex.modal.toString(),
    '--z-index-popover': theme.zIndex.popover.toString(),
    '--z-index-toast': theme.zIndex.toast.toString(),
    '--z-index-tooltip': theme.zIndex.tooltip.toString(),
  };
};

// Apply CSS custom properties to the document root
export const applyCSSCustomProperties = (theme: Theme) => {
  const properties = themeToCSSCustomProperties(theme);
  const root = document.documentElement;

  Object.entries(properties).forEach(([property, value]) => {
    if (value) {
      root.style.setProperty(property, value);
    }
  });
};

// Remove CSS custom properties from the document root
export const removeCSSCustomProperties = () => {
  const root = document.documentElement;
  const properties = Object.keys(themeToCSSCustomProperties({} as Theme));

  properties.forEach((property) => {
    root.style.removeProperty(property);
  });
};

// Detect system dark mode preference
export const getSystemDarkMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// Get stored theme preference
export const getStoredTheme = (storageKey: string = 'magicbi-theme-mode'): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

// Store theme preference
export const setStoredTheme = (mode: string, storageKey: string = 'magicbi-theme-mode'): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, mode);
  } catch {
    // Silently fail if localStorage is not available
  }
};

/** Window event ThemeProvider listens for to drop back to light after a logout. */
export const THEME_RESET_EVENT = 'magicbi:theme-reset';

/**
 * Logout: forget the stored mode, show light at once (so the login screen and the next user
 * start light) and tell a mounted ThemeProvider to reset its own state.
 */
export const resetThemeToLight = (storageKey: string = 'magicbi-theme-mode'): void => {
  if (typeof window === 'undefined') return;
  removeStoredTheme(storageKey);
  document.documentElement.dataset.mbTheme = 'light';
  document.documentElement.setAttribute('data-theme', 'light');
  window.dispatchEvent(new Event(THEME_RESET_EVENT));
};

// Remove stored theme preference
export const removeStoredTheme = (storageKey: string = 'magicbi-theme-mode'): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // Silently fail if localStorage is not available
  }
};

// Create media query listener for system theme changes
export const createSystemThemeListener = (callback: (isDark: boolean) => void) => {
  if (typeof window === 'undefined') return () => {};

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (e: MediaQueryListEvent) => callback(e.matches);

  mediaQuery.addEventListener('change', listener);

  return () => {
    mediaQuery.removeEventListener('change', listener);
  };
};

// Validate theme structure
export const validateTheme = (theme: any): ThemeValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check required properties
  const requiredProperties = [
    'mode',
    'colors',
    'typography',
    'spacing',
    'shadows',
    'borders',
    'transitions',
    'breakpoints',
    'zIndex',
  ];

  requiredProperties.forEach((prop) => {
    if (!theme[prop]) {
      errors.push(`Missing required property: ${prop}`);
    }
  });

  // Check color structure
  if (theme.colors) {
    const requiredColorProperties = [
      'primary',
      'grey',
      'background',
      'surface',
      'border',
      'text',
      'interactive',
      'success',
      'warning',
      'error',
      'info',
    ];

    requiredColorProperties.forEach((prop) => {
      if (!theme.colors[prop]) {
        errors.push(`Missing required color property: colors.${prop}`);
      }
    });
  }

  // Check typography structure
  if (theme.typography) {
    const requiredTypographyProperties = ['display', 'heading', 'body', 'label', 'caption', 'code', 'button'];

    requiredTypographyProperties.forEach((prop) => {
      if (!theme.typography[prop]) {
        errors.push(`Missing required typography property: typography.${prop}`);
      }
    });
  }

  // Validation warnings
  if (theme.mode && !['light', 'dark'].includes(theme.mode)) {
    warnings.push(`Invalid theme mode: ${theme.mode}. Expected 'light' or 'dark'.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

// Get contrasting text color for a background
export const getContrastingTextColor = (backgroundColor: string, theme: Theme): string => {
  // Simple logic - for a complete implementation, you'd want to calculate actual contrast ratios
  const darkColors = ['primary', 'grey'];
  const isBackgroundDark = darkColors.some((color) => backgroundColor.includes(color));

  return isBackgroundDark ? theme.colors.text.inverse : theme.colors.text.primary;
};

// Generate accessible color pairs
export const generateAccessibleColorPairs = (theme: Theme) => {
  return {
    // High contrast pairs for important content
    highContrast: {
      text: theme.colors.text.primary,
      background: theme.colors.background.primary,
    },
    // Medium contrast pairs for secondary content
    mediumContrast: {
      text: theme.colors.text.secondary,
      background: theme.colors.background.secondary,
    },
    // Low contrast pairs for subtle content
    lowContrast: {
      text: theme.colors.text.tertiary,
      background: theme.colors.background.tertiary,
    },
    // Inverse pairs for dark content on light backgrounds or vice versa
    inverse: {
      text: theme.colors.text.inverse,
      background: theme.colors.background.inverse,
    },
  };
};

// Theme debugging utilities
export const debugTheme = (theme: Theme) => {
  console.group('🎨 MagicBI Design System - Theme Debug');
  console.log('Mode:', theme.mode);
  console.log('Colors:', theme.colors);
  console.log('Typography:', theme.typography);
  console.log('Spacing:', theme.spacing);
  console.log('Shadows:', theme.shadows);
  console.log('Borders:', theme.borders);
  console.log('Transitions:', theme.transitions);
  console.log('Breakpoints:', theme.breakpoints);
  console.log('Z-Index:', theme.zIndex);
  
  const validation = validateTheme(theme);
  if (!validation.isValid) {
    console.error('Theme validation errors:', validation.errors);
  }
  if (validation.warnings.length > 0) {
    console.warn('Theme validation warnings:', validation.warnings);
  }
  
  console.groupEnd();
};

// Performance monitoring for theme switches
export const createThemePerformanceMonitor = () => {
  let themeSwitchStartTime: number | null = null;

  return {
    start: () => {
      themeSwitchStartTime = performance.now();
    },
    end: (themeName: string) => {
      if (themeSwitchStartTime) {
        const duration = performance.now() - themeSwitchStartTime;
        console.log(`🚀 Theme switch to ${themeName} completed in ${duration.toFixed(2)}ms`);
        themeSwitchStartTime = null;
      }
    },
  };
};
